import { Plus, Trash2 } from "lucide-react";
import React from "react";
import TravelFormInput from "src/components/travel/form/TravelFormInput";
import TravelFormSelect from "src/components/travel/form/TravelFormSelect";
import { EMPTY_PASSPORT, EMPTY_VISA, EMPTY_IDCARD } from "src/helpers/travelHelper";

const ENTRY_TYPE_OPTIONS = [
  { value: "SINGLE", label: "Single" },
  { value: "MULTIPLE", label: "Multiple" },
];

const TravelPassportDocuments = ({ formData, setFormData, countries = [] }) => {
  const { passports, visas, idCards } = formData.papers;

  const passportOptions = passports.filter((p) => p.number && p?.uuid).map((p) => ({ value: p?.uuid, label: `Passport: ${p.number}` }));

  const handlePaperChange = (type, index, field, value) => {
    const updatedArray = [...formData.papers[type]];

    if (type === "passports" && field === "primary" && value === true) {
      updatedArray.forEach((p) => (p.primary = false));
    }

    updatedArray[index] = { ...updatedArray[index], [field]: value };
    setFormData({ ...formData, papers: { ...formData.papers, [type]: updatedArray } });
  };

  const addPaper = (type, template) => {
    setFormData({ ...formData, papers: { ...formData.papers, [type]: [...formData.papers[type], template] } });
  };

  const removePaper = (type, index) => {
    const item = formData.papers[type][index];
    let updatedArray = [...formData.papers[type]];
    if (item.uuid) {
      updatedArray[index] = { ...item, _operation: "remove" };
    } else {
      updatedArray = updatedArray.filter((_, i) => i !== index);
    }
    setFormData({ ...formData, papers: { ...formData.papers, [type]: updatedArray } });
  };

  return (
    <div className="col-span-12 space-y-10">
      <div>
        <h4 className="mb-4 border-b border-gray-100 pb-2 text-lg font-bold text-[#2D0B31]">Passport(s)</h4>
        {passports.map((item, index) => {
          if (item._operation === "remove") return null;
          return (
            <div
              key={index}
              className="group relative mb-6 rounded-xl border border-gray-200 bg-gray-50/50 p-5 transition-all hover:border-[#2D0B31]/30">
              <div className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-2">
                <TravelFormSelect
                  label="Nationality"
                  select_type="country"
                  options={countries}
                  value={item.country}
                  onChange={(e) => handlePaperChange("passports", index, "country", e.target.value)}
                  required
                />
                <TravelFormInput
                  label="Passport No."
                  value={item.number}
                  onChange={(e) => handlePaperChange("passports", index, "number", e.target.value)}
                  required
                />
                <TravelFormInput
                  label="Issue Date"
                  type="date"
                  value={item.issueDate}
                  onChange={(e) => handlePaperChange("passports", index, "issueDate", e.target.value)}
                />
                <TravelFormInput
                  label="Expires"
                  type="date"
                  value={item.expiration}
                  onChange={(e) => handlePaperChange("passports", index, "expiration", e.target.value)}
                  required
                />
                <TravelFormSelect
                  label="Issue Country"
                  select_type="country"
                  options={countries}
                  value={item.issueCountry}
                  onChange={(e) => handlePaperChange("passports", index, "issueCountry", e.target.value)}
                />
                <TravelFormInput
                  label="Issue Place"
                  value={item.issuePlace}
                  onChange={(e) => handlePaperChange("passports", index, "issuePlace", e.target.value)}
                />

                <div className="col-span-1 mt-2 flex items-center gap-2 gap-x-3 md:col-span-2">
                  <button
                    type="button"
                    onClick={() => removePaper("passports", index)}
                    className="rounded-lg border p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500">
                    <Trash2 size={18} />
                  </button>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`primary-pass-${index}`}
                      checked={item.primary}
                      onChange={(e) => handlePaperChange("passports", index, "primary", e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-[#2D0B31] focus:ring-[#2D0B31]"
                    />
                    <label
                      htmlFor={`primary-pass-${index}`}
                      className="cursor-pointer text-sm font-bold text-gray-700">
                      Primary Passport
                    </label>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        <button
          type="button"
          onClick={() => addPaper("passports", EMPTY_PASSPORT)}
          className="flex items-center gap-2 rounded-lg bg-[#2D0B31]/5 px-4 py-2 text-sm font-bold text-[#2D0B31] transition-colors hover:text-[#4a1352]">
          <Plus size={16} /> Add new passport
        </button>
      </div>

      <div>
        <h4 className="mb-4 border-b border-gray-100 pb-2 text-lg font-bold text-[#2D0B31]">Visa Information</h4>
        {visas.map((item, index) => {
          if (item._operation === "remove") return null;
          return (
            <div
              key={index}
              className="group relative mb-6 rounded-xl border border-gray-200 bg-gray-50/50 p-5 transition-all hover:border-[#2D0B31]/30">
              <div className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-2">
                <TravelFormSelect
                  label="Country"
                  select_type="country"
                  options={countries}
                  value={item.country}
                  onChange={(e) => handlePaperChange("visas", index, "country", e.target.value)}
                  required
                />
                <TravelFormInput
                  label="Number"
                  value={item.number}
                  onChange={(e) => handlePaperChange("visas", index, "number", e.target.value)}
                  required
                />
                <TravelFormInput
                  label="Issue Date"
                  type="date"
                  value={item.issueDate}
                  onChange={(e) => handlePaperChange("visas", index, "issueDate", e.target.value)}
                />
                <TravelFormInput
                  label="Expires"
                  type="date"
                  value={item.expiration}
                  onChange={(e) => handlePaperChange("visas", index, "expiration", e.target.value)}
                  required
                />
                <TravelFormSelect
                  label="Entry Type"
                  options={ENTRY_TYPE_OPTIONS}
                  value={item.entryType}
                  onChange={(e) => handlePaperChange("visas", index, "entryType", e.target.value)}
                />
                <TravelFormSelect
                  label="Linked Passport"
                  options={passportOptions}
                  value={item.linkedPassport}
                  onChange={(e) => handlePaperChange("visas", index, "linkedPassport", e.target.value)}
                />
              </div>
              <button
                type="button"
                onClick={() => removePaper("visas", index)}
                className="mt-4 rounded-lg border p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500">
                <Trash2 size={18} />
              </button>
            </div>
          );
        })}
        <button
          type="button"
          onClick={() => addPaper("visas", EMPTY_VISA)}
          className="flex items-center gap-2 rounded-lg bg-[#2D0B31]/5 px-4 py-2 text-sm font-bold text-[#2D0B31] transition-colors hover:text-[#4a1352]">
          <Plus size={16} /> Add new visa
        </button>
      </div>

      <div>
        <h4 className="mb-4 border-b border-gray-100 pb-2 text-lg font-bold text-[#2D0B31]">Identification Cards</h4>
        {idCards.map((item, index) => {
          if (item._operation === "remove") return null;
          return (
            <div
              key={index}
              className="group relative mb-6 rounded-xl border border-gray-200 bg-gray-50/50 p-5 transition-all hover:border-[#2D0B31]/30">
              <div className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-2">
                <TravelFormSelect
                  label="Country"
                  select_type="country"
                  options={countries}
                  value={item.country}
                  onChange={(e) => handlePaperChange("idCards", index, "country", e.target.value)}
                  required
                />
                <TravelFormInput
                  label="Number"
                  value={item.number}
                  onChange={(e) => handlePaperChange("idCards", index, "number", e.target.value)}
                  required
                />
                <TravelFormInput
                  label="Issue Date"
                  type="date"
                  value={item.issueDate}
                  onChange={(e) => handlePaperChange("idCards", index, "issueDate", e.target.value)}
                />
                <TravelFormInput
                  label="Expires"
                  type="date"
                  value={item.expiration}
                  onChange={(e) => handlePaperChange("idCards", index, "expiration", e.target.value)}
                  required
                />
              </div>
              <button
                type="button"
                onClick={() => removePaper("idCards", index)}
                className="mt-4 rounded-lg border p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500">
                <Trash2 size={18} />
              </button>
            </div>
          );
        })}
        <button
          type="button"
          onClick={() => addPaper("idCards", EMPTY_IDCARD)}
          className="flex items-center gap-2 rounded-lg bg-[#2D0B31]/5 px-4 py-2 text-sm font-bold text-[#2D0B31] transition-colors hover:text-[#4a1352]">
          <Plus size={16} /> Add new ID card
        </button>
      </div>
    </div>
  );
};

export default TravelPassportDocuments;
